import { HTTPError } from 'ky';
import { bopApi, cryptoInfo } from '@/lib/api/client';
import { isNotFoundError } from '@/lib/api/error';

const GET_LOANS_PATH = 'v1/system/getLoans';
const UPDATE_LOAN_PATH = 'v1/system/updateLoan';
const NEW_LOAN_PATH = 'v1/system/newLoan';
// Fix: legacy hard-coded the PRODUCTION crypto-info URL
// (https://api.tradingone.io/crypto-info/v1/getPrices) in the add-asset flow, making
// non-prod behave like prod — the env-configured cryptoInfo instance is used instead.
const CRYPTO_PRICES_PATH = 'v1/getPrices';

export interface LoanCurrencyPosition {
  /** Credit line size in native units (raw number — format at render) */
  quantity: number;
  /** quantity × current EUR price — 0 when crypto-info has no price */
  inEuros: number;
}

export interface ClientLoan {
  /** Display nickname, e.g. "acme" */
  clientName: string;
  /** Client PID (UUID) */
  accountId: string;
  /** Keyed by currency code, e.g. "BTC" */
  loans: Record<string, LoanCurrencyPosition>;
}

export interface GetLoansResponse {
  loans: ClientLoan[];
  /** Σ inEuros across ALL clients and currencies (raw number — format at render) */
  totalEuroAmount: number;
}

export interface UpdateLoanRequest {
  clientName: string;
  /** Wire key is `clientPID` here but `pid` on newLoan — kept as-is */
  clientPID: string;
  /** The client's ENTIRE desired end state; deleting a credit line = OMIT its key */
  loans: Record<string, number>;
  /** Audit justification */
  comment: string;
}

export interface NewLoanRequest {
  pid: string;
  name: string;
  comment: string;
  /** Optional on the wire; the BOP add-institution flow never sends it */
  loans?: Record<string, number>;
}

export interface CryptoPrice {
  symbol: string;
  eur: number;
  [additionalData: string]: unknown;
}

// Stable reference for the unseeded-environment empty state
const EMPTY_LOANS_RESPONSE: GetLoansResponse = { loans: [], totalEuroAmount: 0 };

export const getLoans = async (): Promise<GetLoansResponse> => {
  try {
    return await bopApi.get(GET_LOANS_PATH).json<GetLoansResponse>();
  } catch (error) {
    // 404 'No reserved balances found' means the record was never seeded —
    // treat as "no institutions yet" (Add Institution bootstraps it via newLoan).
    if (isNotFoundError(error)) return EMPTY_LOANS_RESPONSE;
    throw error;
  }
};

/**
 * Console breadcrumbs for credit-line mutations. These calls fan out server-side
 * to the cross-account funds ledger, where rejections have historically surfaced
 * only in the funds Lambda's CloudWatch logs — the request/outcome pair here ties
 * an opaque UI toast back to the exact payload that triggered it.
 */
const trackLoanMutation = async <T>(action: string, details: Record<string, unknown>, execute: () => Promise<T>): Promise<T> => {
  console.info(`[loans] ${action} request`, details);
  try {
    const response = await execute();
    console.info(`[loans] ${action} succeeded`, details);
    return response;
  } catch (error) {
    console.error(`[loans] ${action} failed`, {
      ...details,
      status: error instanceof HTTPError ? error.response.status : undefined,
      // parseApiErrorHook promotes the server `{ error }` body into message
      message: error instanceof Error ? error.message : String(error),
    });
    throw error;
  }
};

export const updateLoan = (body: UpdateLoanRequest) =>
  trackLoanMutation('updateLoan', { clientPID: body.clientPID, clientName: body.clientName, loans: body.loans }, () =>
    bopApi.post(UPDATE_LOAN_PATH, { json: body }).json<Record<string, never>>(),
  );

export const newLoan = (body: NewLoanRequest) =>
  trackLoanMutation('newLoan', { pid: body.pid, name: body.name, loans: body.loans }, () => bopApi.post(NEW_LOAN_PATH, { json: body }).json<Record<string, never>>());

/** Unauthenticated crypto-info call — asset options for the add-asset dialog */
export const getCryptoPrices = () => cryptoInfo.get(CRYPTO_PRICES_PATH).json<CryptoPrice[]>();

export type LoanChange = { kind: 'set'; currency: string; quantity: number } | { kind: 'remove'; currency: string };

/**
 * Build the full-state upsert payload from the displayed client.
 * Immutable by construction (fix: legacy mutated the rendered object, so a
 * failed request corrupted the card): quantities are re-derived from the fresh query
 * data and `inEuros`/`eurosTotal`/`accountId` can never leak into `loans` (the server
 * would treat unknown keys as currencies and Zod-fail on object values).
 */
export const buildUpdateLoanRequest = (client: ClientLoan, change: LoanChange, comment: string): UpdateLoanRequest => {
  const currentQuantities = Object.fromEntries(Object.entries(client.loans).map(([currency, position]) => [currency, position.quantity]));
  const loans =
    change.kind === 'set'
      ? { ...currentQuantities, [change.currency]: change.quantity }
      : // There is no delete endpoint — omitting the key books the decrease to 0
        Object.fromEntries(Object.entries(currentQuantities).filter(([currency]) => currency !== change.currency));
  return { clientName: client.clientName, clientPID: client.accountId, loans, comment };
};

/** Per-client EUR total is computed client-side, never returned by the API */
export const getClientEurosTotal = (client: ClientLoan): number => Object.values(client.loans).reduce((total, position) => total + position.inEuros, 0);

// Fix: legacy char-whitelist [0-9.] accepted '', '.', '1.2.3' (parseFloat
// then produced NaN → JSON null → opaque server 400). A strict decimal check instead.
export const QUANTITY_PATTERN = /^\d+(\.\d+)?$/;

/**
 * Zero stays allowed on UPDATE (flagged deviation kept: the server accepts
 * non-negative and a 0 books the decrease); ISSUE requires > 0.
 */
export const isValidQuantity = (value: string, { allowZero }: { allowZero: boolean }): boolean => {
  const trimmed = value.trim();
  if (!QUANTITY_PATTERN.test(trimmed)) return false;
  return allowZero || Number.parseFloat(trimmed) > 0;
};

// Fix: legacy detected authorisation failures by matching a literal error
// string minted by its Nuxt layer — branch on the HTTP status instead (delegated to
// the shared lib/api/error predicate; re-exported for the credit-lines call sites).
export { isForbiddenError } from '@/lib/api/error';

const INSUFFICIENT_FUNDS_ERROR = 'INSUFFICIENT_FUNDS';
// The raw code is opaque — most visible on delete, where the server
// books an outgoing adjustment for the full amount the client may have spent.
const INSUFFICIENT_FUNDS_MESSAGE = 'Client has insufficient available balance to reduce this credit line';

// ky's default HTTPError message when the body carried no `{ error }` to promote
const KY_DEFAULT_MESSAGE_PATTERN = /^Request failed with status code/;

/** Surface the server `{ error }` when present, else the flow's fallback */
export const getLoanMutationErrorMessage = (error: unknown, fallback: string): string => {
  if (!(error instanceof HTTPError)) return fallback;
  const { message } = error;
  if (message === INSUFFICIENT_FUNDS_ERROR) return INSUFFICIENT_FUNDS_MESSAGE;
  if (!message || KY_DEFAULT_MESSAGE_PATTERN.test(message)) return fallback;
  return message;
};
